import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useToast } from '@/hooks/use-toast';
import PageHeader from '@/components/shared/PageHeader';
import {
  Bell, Palette, Volume2, Shield, Lock, ChevronDown, ChevronRight,
  ArrowLeft, Smartphone, Monitor, Moon, Sun,
} from 'lucide-react';

interface SectionProps {
  icon: typeof Bell;
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function SettingsSection({ icon: Icon, title, defaultOpen = false, children }: SectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <button className="flex w-full items-center gap-3 p-4 text-left transition-colors hover:bg-muted/50">
            <Icon className="h-5 w-5 text-primary" />
            <span className="flex-1 text-sm font-semibold text-foreground">{title}</span>
            {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="border-t pt-4">{children}</CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

function ToggleRow({ label, description, checked, onChange }: { label: string; description?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <div>
        <p className="text-sm font-medium text-foreground">{label}</p>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

export default function ProfileSettingsPage() {
  const { toast } = useToast();
  const navigate = useNavigate();

  // Notification toggles
  const [emailNotifs, setEmailNotifs] = useState(true);
  const [pushNotifs, setPushNotifs] = useState(true);
  const [smsNotifs, setSmsNotifs] = useState(false);
  const [woCreated, setWoCreated] = useState(true);
  const [intakeComplete, setIntakeComplete] = useState(true);
  const [exceptionAlert, setExceptionAlert] = useState(true);

  // Appearance
  const [theme, setTheme] = useState('system');

  // Sound
  const [scanVolume, setScanVolume] = useState([70]);
  const [hapticFeedback, setHapticFeedback] = useState(true);

  // Privacy
  const [activityVisible, setActivityVisible] = useState(true);
  const [onlineStatus, setOnlineStatus] = useState(true);
  const [readReceipts, setReadReceipts] = useState(false);

  // Security
  const [twoFactor, setTwoFactor] = useState(false);

  const save = (section: string) => {
    toast({ title: `${section} saved`, description: 'Your preferences have been updated.' });
  };

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => navigate('/profile')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <PageHeader title="Profile Settings" subtitle="Manage your preferences and privacy" />
      </div>

      {/* Notifications */}
      <SettingsSection icon={Bell} title="Notifications" defaultOpen>
        <div className="space-y-1">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Channels</p>
          <ToggleRow label="Email notifications" description="Receive updates via email" checked={emailNotifs} onChange={setEmailNotifs} />
          <ToggleRow label="Push notifications" description="Native device notifications" checked={pushNotifs} onChange={setPushNotifs} />
          <ToggleRow label="SMS notifications" description="Text message alerts" checked={smsNotifs} onChange={setSmsNotifs} />
        </div>
        <div className="mt-4 space-y-1">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Events</p>
          <ToggleRow label="Work order created" checked={woCreated} onChange={setWoCreated} />
          <ToggleRow label="Intake completed" checked={intakeComplete} onChange={setIntakeComplete} />
          <ToggleRow label="Exception alerts" checked={exceptionAlert} onChange={setExceptionAlert} />
        </div>
        <Button size="sm" className="mt-4" onClick={() => save('Notifications')}>Save</Button>
      </SettingsSection>

      {/* Appearance */}
      <SettingsSection icon={Palette} title="Appearance">
        <div className="space-y-3">
          <Label className="text-sm font-medium">Theme</Label>
          <RadioGroup value={theme} onValueChange={setTheme} className="grid grid-cols-3 gap-3">
            {[
              { value: 'light', label: 'Light', icon: Sun },
              { value: 'dark', label: 'Dark', icon: Moon },
              { value: 'system', label: 'System', icon: Monitor },
            ].map((opt) => (
              <Label
                key={opt.value}
                className={`flex cursor-pointer flex-col items-center gap-2 rounded-lg border p-4 transition-colors ${
                  theme === opt.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                }`}
              >
                <RadioGroupItem value={opt.value} className="sr-only" />
                <opt.icon className="h-5 w-5" />
                <span className="text-xs font-medium">{opt.label}</span>
              </Label>
            ))}
          </RadioGroup>
        </div>
        <Button size="sm" className="mt-4" onClick={() => save('Appearance')}>Save</Button>
      </SettingsSection>

      {/* Sound & Haptics */}
      <SettingsSection icon={Volume2} title="Sound & Haptics">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Scan beep volume</Label>
            <div className="flex items-center gap-3">
              <Volume2 className="h-4 w-4 text-muted-foreground" />
              <Slider value={scanVolume} onValueChange={setScanVolume} max={100} step={5} className="flex-1" />
              <span className="w-8 text-right text-xs font-medium text-muted-foreground">{scanVolume[0]}%</span>
            </div>
          </div>
          <ToggleRow label="Haptic feedback" description="Vibrate on scan and actions" checked={hapticFeedback} onChange={setHapticFeedback} />
        </div>
        <Button size="sm" className="mt-4" onClick={() => save('Sound & Haptics')}>Save</Button>
      </SettingsSection>

      {/* Privacy */}
      <SettingsSection icon={Shield} title="Privacy">
        <div className="space-y-1">
          <ToggleRow label="Activity visible to team" description="Others can see your recent activity" checked={activityVisible} onChange={setActivityVisible} />
          <ToggleRow label="Show online status" description="Display when you're active" checked={onlineStatus} onChange={setOnlineStatus} />
          <ToggleRow label="Read receipts" description="Let others know when you've read messages" checked={readReceipts} onChange={setReadReceipts} />
        </div>
        <Button size="sm" className="mt-4" onClick={() => save('Privacy')}>Save</Button>
      </SettingsSection>

      {/* Security */}
      <SettingsSection icon={Lock} title="Security">
        <div className="space-y-3">
          <ToggleRow label="Two-factor authentication" description="Add an extra layer of security" checked={twoFactor} onChange={setTwoFactor} />
          <Button variant="outline" size="sm" onClick={() => toast({ title: 'Change password', description: 'Password change will be available when connected to backend.' })}>
            Change Password
          </Button>
          <div className="mt-4 rounded-lg border p-3">
            <p className="text-xs font-semibold text-muted-foreground">Active Sessions</p>
            <div className="mt-2 flex items-center gap-3">
              <Smartphone className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium text-foreground">Current device</p>
                <p className="text-xs text-muted-foreground">Last active: Now</p>
              </div>
            </div>
          </div>
        </div>
        <Button size="sm" className="mt-4" onClick={() => save('Security')}>Save</Button>
      </SettingsSection>
    </div>
  );
}
